
import { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { Loading } from "../../../componentes/loading/loading";

export default function DashboardBPFilial({ dados, loading }) {
    // `dados` é o objeto vindo da API: { "Filial X": 1234.56, "Filial Y": "789.10", ... }
    // Converte para pares [label, total], trata BigDecimal string e ordena por valor desc (opcional)
    const entries = useMemo(() => {
        if (!dados) return [];
        return Object.entries(dados)
            .map(([label, total]) => [
                label || "Não identificado",
                typeof total === "number" ? total : Number(total) || 0,
            ])
            .sort((a, b) => b[1] - a[1]); // ordenar por valor desc
    }, [dados]);

    const labels = entries.map(([label]) => label);
    const series = entries.map(([, total]) => total);

    const colorPalette = [
        "#4F46E5", "#10B981", "#F59E0B", "#EF4444", "#6366F1",
        "#3B82F6", "#EC4899", "#14B8A6", "#8B5CF6", "#F97316",
        "#06B6D4", "#84CC16", "#A855F7", "#E11D48", "#0EA5E9",
    ];

    const [isLoading, setIsLoading] = useState(true);

    const isEmpty =
        !series ||
        series.length === 0 ||
        series.every((v) => !Number.isFinite(v) || v === 0);

    useEffect(() => {
        const timeout = setTimeout(() => setIsLoading(false), 1000);
        return () => clearTimeout(timeout);
    }, [series]);

    function gerarMapaDeCores(labels) {
        const map = {};
        labels.forEach((label, index) => {
            map[label] = colorPalette[index % colorPalette.length];
        });
        return map;
    }

    const colorMap = gerarMapaDeCores(labels);

    const options = {
        chart: {
            type: "pie",
            height: 220,
            toolbar: { show: false },
        },
        labels,
        colors: labels.map((label) => colorMap[label]),
        stroke: { width: 0 }, // remove linha branca
        legend: {
            show: false,
            position: "bottom",
            fontSize: "11px",
            itemMargin: { vertical: 2 },
        },
        tooltip: {
            y: {
                formatter: (val) => "R$ " + Number(val).toLocaleString("pt-BR"),
            },
        },
        dataLabels: { enabled: true },
    };

    if (loading) {
        return <div className="w-[260px] h-[350px]">
            <Loading />
        </div>
    }

    return (
          <div className="w-[260px] h-[350px]">
            <h2 className="font-display text-base font-semibold mb-1 text-center text-foreground">
                Totais por Filial
            </h2>

            {isLoading && (
                <div className="flex justify-center h-[187px] items-center">
                    <div className="animate-pulse bg-muted rounded-[50%] h-[164px]  w-[164px]" />
                </div>
            )}

            {/* Conteúdo com fade-in após loading */}
            {!isLoading && (
                <div className="animate-fade-in">
                    {isEmpty ? (
                        <div className="flex flex-col items-center justify-center h-[187px] text-muted-foreground">
                            <i className="fa fa-exclamation-circle text-center text-[120px] opacity-80 mb-2" />
                            <p className="text-sm">Não há valores para gerar este gráfico</p>
                        </div>
                    ) : (
                        <Chart options={options} series={series} type="pie" height={187} />
                    )}
                </div>
            )}

            {/* Legenda completa com scroll */}
            <div
                className="overflow-y-auto mt-1 text-center px-2"
                style={{ maxHeight: "120px", fontSize: "11px" }}
            >
                <ul className="space-y-1">
                    {labels.map((x, index) => (
                        <li
                            key={index}
                            className="truncate flex items-center justify-between cursor-default"
                            title={`${x} - R$ ${Number(series[index]).toLocaleString("pt-BR")}`}
                        >
                            <div className="flex items-center space-x-2">
                                <span
                                    className="inline-block w-3 h-3 rounded-full"
                                    style={{ backgroundColor: colorMap[x] }}
                                />
                                <span className="font-medium">{x}</span>
                            </div>
                            <span>R$ {Number(series[index]).toLocaleString("pt-BR")}</span>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}
